import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { pumpAnalysisBaseSchema, InsertPumpAnalysis } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCustomer } from "@/hooks/use-customer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { SaveIcon, ArrowLeft, ArrowRight, Send } from "lucide-react";
import { z } from "zod";
import { StepIndicator } from "./step-indicators";
import {
  Step1PumpInfo,
  Step2ApplicationDetails,
  Step3LoadProfile,
  Step4OperationMaintenance,
  Step5ElectricalSetup,
  Step6UtilityInfo,
  Step7FacilityDetails,
  Step8Controls,
  Step9RetrofitConstraints,
  Step10Documents,
  Step11Analytics
} from "./pump-form-steps";

// Define the schema for the complete form
const formSchema = pumpAnalysisBaseSchema;

// Default values for the form
const defaultLoadProfile = [
  { loadRange: "0-10%", hours: 0 },
  { loadRange: "11-20%", hours: 0 },
  { loadRange: "21-30%", hours: 0 },
  { loadRange: "31-40%", hours: 0 },
  { loadRange: "41-50%", hours: 0 },
  { loadRange: "51-60%", hours: 0 },
  { loadRange: "61-70%", hours: 0 },
  { loadRange: "71-80%", hours: 0 },
  { loadRange: "81-90%", hours: 0 },
  { loadRange: "91-100%", hours: 0 }
];

const defaultSensitivityVariables = [
  { name: "utilityRate", selected: true },
  { name: "operatingHours", selected: true },
  { name: "maintenanceCost", selected: false },
  { name: "discountRate", selected: false }
];

// Define default other costs with proper type
const defaultOtherCosts: { description: string; amount: number; recurring: boolean }[] = [];

const defaultMonteCarloDistributions = [
  { name: "kWh Savings", mean: 20000, stdDev: 2000 },
  { name: "Maintenance Cost", mean: 500, stdDev: 50 }
];

const defaultValues: Partial<z.infer<typeof formSchema>> = {
  loadProfile: defaultLoadProfile,
  lifecycleYears: 10,
  discountRate: 3.5,
  inflationRate: 2.0,
  carbonEmissionFactor: 0.5,
  sensitivityVariables: defaultSensitivityVariables,
  monteCarloDistributions: defaultMonteCarloDistributions,
  otherCosts: defaultOtherCosts,
  // Default values for simplified load profile
  loadProfileSimplifiedSummer: 40,
  loadProfileSimplifiedSpringFall: 35,
  loadProfileSimplifiedWinter: 25
};

export function PumpForm({ initialData }: { initialData?: Partial<z.infer<typeof formSchema>> }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { activeCustomerId } = useCustomer();
  const [currentStep, setCurrentStep] = useState(1);
  const [datasheet, setDatasheet] = useState<File | null>(null);
  
  const totalSteps = 11;
  
  // Function to get full step titles using i18n
  const getStepFullTitle = (step: number): string => {
    switch (step) {
      case 1: return t("form.steps.pumpInfo");
      case 2: return t("form.steps.application");
      case 3: return t("form.steps.loadProfile");
      case 4: return t("form.steps.operation");
      case 5: return t("form.steps.electrical");
      case 6: return t("form.steps.utility");
      case 7: return t("form.steps.facility");
      case 8: return t("form.steps.controls");
      case 9: return t("form.steps.retrofit");
      case 10: return t("form.steps.documents");
      case 11: return t("form.steps.analytics");
      default: return t("form.steps.stepX", { step });
    }
  };

  // Set up the form
  const methods = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { ...defaultValues, ...initialData },
    mode: "onChange"
  });

  // Re-initialize form values when initialData populates (e.g. from state resolving dynamically)
  useEffect(() => {
    if (initialData) {
      methods.reset({ ...defaultValues, ...initialData });
    }
  }, [initialData, methods]);

  
  const { handleSubmit, formState: { errors, isValid, isDirty } } = methods;
  
  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (data: InsertPumpAnalysis & { customerId?: string }) => {
      const payload = activeCustomerId ? { ...data, customerId: activeCustomerId } : data;
      const res = await apiRequest("POST", "/api/analyses", payload);
      return await res.json();
    },
    onSuccess: (data) => {
      toast({
        title: t("form.draftSaved"),
        description: t("form.draftSavedDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/analyses"] });
    },
    onError: (error: Error) => {
      toast({
        title: t("form.draftSaveError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Submit analysis mutation
  const submitAnalysisMutation = useMutation({
    mutationFn: async (data: InsertPumpAnalysis & { customerId?: string }) => {
      // First create or update the analysis
      const payload = activeCustomerId ? { ...data, customerId: activeCustomerId } : data;
      const res = await apiRequest("POST", "/api/analyses", payload);
      const analysis = await res.json();
      
      // Upload datasheet if present
      if (datasheet) {
        const formData = new FormData();
        formData.append("datasheet", datasheet);
        await apiRequest("POST", `/api/analyses/${analysis.id}/datasheet`, formData);
      }
      
      // Submit the analysis for processing
      const submitRes = await apiRequest("POST", `/api/analyses/${analysis.id}/submit`);
      return await submitRes.json();
    },
    onSuccess: (data) => {
      toast({
        title: t("form.analysisSubmitted"),
        description: t("form.analysisSubmittedDescription"),
      });
      queryClient.invalidateQueries({ queryKey: ["/api/analyses"] });
      navigate(`/analysis/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: t("form.submissionError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Function to get translated step title
  const getStepTitle = (step: number): string => {
    switch (step) {
      case 1: return t("form.steps.pumpInfo");
      case 2: return t("form.steps.application");
      case 3: return t("form.steps.loadProfile");
      case 4: return t("form.steps.operation");
      case 5: return t("form.steps.electrical");
      case 6: return t("form.steps.utility");
      case 7: return t("form.steps.facility");
      case 8: return t("form.steps.controls");
      case 9: return t("form.steps.retrofit");
      case 10: return t("form.steps.documents");
      case 11: return t("form.steps.analytics");
      default: return t("form.steps.stepX", { step });
    }
  };
  
  // Form submission handler
  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // User ID will be added on the server side
    if (currentStep === totalSteps) {
      submitAnalysisMutation.mutate(data as InsertPumpAnalysis);
    } else {
      // Move to next step
      setCurrentStep(prev => prev + 1);
    }
  };
  
  // Save draft handler
  const onSaveDraft = () => {
    const currentData = methods.getValues();
    saveDraftMutation.mutate(currentData as InsertPumpAnalysis);
  };
  
  // Handle datasheet upload
  const onFileChange = (file: File | null) => {
    setDatasheet(file);
  };
  
  // Progress calculation
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
  
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold text-gray-800">
                  {t("form.step")} {currentStep} {t("form.of")} {totalSteps}: {getStepFullTitle(currentStep)}
                </h2>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSaveDraft}
                  disabled={saveDraftMutation.isPending}
                  className="flex items-center gap-2"
                >
                  <SaveIcon className="h-4 w-4" />
                  Save Draft
                </Button>
              </div>
              <Progress value={progress} className="h-2.5" />
            </div>
            
            <StepIndicator
              currentStep={currentStep}
              totalSteps={totalSteps}
              onChange={setCurrentStep}
            />
            
            {/* Step content */}
            {currentStep === 1 && <Step1PumpInfo />}
            {currentStep === 2 && <Step2ApplicationDetails />}
            {currentStep === 3 && <Step3LoadProfile />}
            {currentStep === 4 && <Step4OperationMaintenance />}
            {currentStep === 5 && <Step5ElectricalSetup />}
            {currentStep === 6 && <Step6UtilityInfo />}
            {currentStep === 7 && <Step7FacilityDetails />}
            {currentStep === 8 && <Step8Controls />}
            {currentStep === 9 && <Step9RetrofitConstraints />}
            {currentStep === 10 && <Step10Documents onFileChange={onFileChange} />}
            {currentStep === 11 && <Step11Analytics />}
            
            {/* Navigation buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(prev => Math.max(prev - 1, 1))}
                disabled={currentStep === 1}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("form.navigation.previous")}
              </Button>
              
              <div className="flex gap-2">
                {currentStep < totalSteps ? (
                  <Button 
                    type="submit"
                    className="flex items-center gap-2" 
                  >
                    {t("form.navigation.next")}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    type="submit"
                    className="flex items-center gap-2"
                    disabled={submitAnalysisMutation.isPending}
                  >
                    {t("analysis.submit")}
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </FormProvider>
  );
}
